/**
 * Drug-label helpers. Matrix labels are concentration-specific strings like
 * "Adrenalin 120 µg/ml" or "Aggrastat (Tirofiban) 50 µg/ml". The grid shows a
 * compact name on the header and the concentration on a quieter sub-line.
 *
 * Parsing is best-effort and purely cosmetic: it never changes identity
 * (`drug.id` stays the full label). When no concentration tail is detected the
 * whole label is the name and the sub-line is empty.
 */

import type { CompatibilityDrug } from './types'

// Trailing concentration: a number (with optional decimal) followed by a unit
// expression that ends the string, e.g. "120 µg/ml", "1 mg/ml", "20 mg/8 ml",
// "1 E/ml", "18.75 µg/ml", and percent strengths "1%", "0.9%", "5%". Anchored to
// the end so brand names with numbers (rare here) are not mistaken for one.
const CONCENTRATION_TAIL =
  /\s+(\d+(?:\.\d+)?\s*(?:%|[µμa-zA-Z]+(?:\/\d*\s*[µμa-zA-Z]+)?))\s*$/u

export interface SplitLabel {
  /** Brand/generic name with the concentration tail removed. */
  name: string
  /** The concentration tail (may be empty). */
  concentration: string
}

export function splitLabel(label: string): SplitLabel {
  const m = label.match(CONCENTRATION_TAIL)
  if (!m) return { name: label, concentration: '' }
  return {
    name: label.slice(0, m.index).trim(),
    concentration: m[1].trim(),
  }
}

/** Short header label — name without concentration (or the sentinel label). */
export function shortLabel(drug: CompatibilityDrug): string {
  if (drug.sentinel) return drug.label
  return splitLabel(drug.label).name || drug.label
}

/**
 * Tightest display label for the dense inline matrix: the INN where the brand
 * exposes one in parens ("Lasix (Furosemid) 10 mg/ml" → "Furosemid"), else the
 * name with its concentration tail removed ("Milrinon 200 µg/ml" → "Milrinon").
 * Display-only — identity always stays `drug.id`.
 */
export function innLabel(drug: CompatibilityDrug): string {
  if (drug.sentinel) return drug.label
  const name = splitLabel(drug.label).name || drug.label
  const paren = /\(([^)]+)\)/.exec(name)
  return (paren?.[1] ?? name).trim()
}
